import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { HouseholdDocumentsViewerProvider } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.provider';
import { HouseholdDocumentsViewer } from '@/modules/documents/components/HouseholdDocumentsViewer/HouseholdDocumentsViewer';
import { AnswerReviewWorkbench } from '@/modules/forms/answerReview/AnswerReviewWorkbench';
import { AnswerReviewWorkbenchState } from '@/modules/forms/answerReview/AnswerReviewWorkbench.types';
import { formSubmissionAnswerReviewAdapter } from '@/modules/forms/answerReview/formSubmissionAnswerReview.adapter';
import { FormSubmissionAdapterError } from '@/modules/forms/answerReview/formSubmissionFields';
import { useFormSubmissionCitations } from '@/modules/forms/answerReview/hooks/useFormSubmissionCitations';
import { HouseholdWorkflowTrackersProvider } from '@/modules/workflowTrackers/contexts/HouseholdWorkflowTrackersContext.provider';
import { ROUTE_KEYS } from '@/navigation/constants';
import { FormVersionKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { useAdminFormTestSubmissionQuery } from './AdminFormTests.generated';
const INVALID_ROUTE_MESSAGE = 'This test submission could not load. Check the form, test version, and submission links.';

/** Displays immutable AI output for one form extraction test. */
export function AdminFormTestSubmissionPage() {
  const $ = _c(21);
  const formId = useRequiredParam("formId");
  const versionId = useRequiredParam("versionId");
  const submissionId = useRequiredParam("submissionId");
  const {
    navigate
  } = useNavigateToRoute();
  let t0;
  if ($[0] !== submissionId) {
    t0 = {
      variables: {
        submissionID: submissionId
      },
      fetchPolicy: "network-only",
      pollInterval: 5000
    };
    $[0] = submissionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const query = useAdminFormTestSubmissionQuery(t0);
  const submission = query.data?.node?.__typename === "Submission" ? query.data.node : undefined;
  const routeIsValid = submission?.id === submissionId && submission?.formVersion.kind === FormVersionKind.Test && submission.formVersion.id === versionId && submission.formVersion.form.id === formId;
  let t1;
  bb0: {
    if (query.loading) {
      let t2;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          kind: "loading"
        };
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = t2;
      break bb0;
    }
    if (query.error || !submission || !routeIsValid) {
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = {
          kind: "error",
          message: INVALID_ROUTE_MESSAGE
        };
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2;
      break bb0;
    }
    ;
    try {
      let t3;
      if ($[4] !== submission) {
        t3 = formSubmissionAnswerReviewAdapter({
          submission,
          saveState: {
            kind: "saved"
          },
          readOnly: true
        });
        $[4] = submission;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      t1 = t3;
    } catch (t2) {
      const error = t2;
      if (!(error instanceof FormSubmissionAdapterError)) {
        throw error;
      }
      let t3;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          kind: "unsupported",
          message: "This submission contains answer data that this review cannot display."
        };
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      t1 = t3;
    }
  }
  const state = t1;
  const sources = getNodes(submission?.sourceDocuments);
  const citations = useFormSubmissionCitations({
    sources,
    state,
    canAdd: false,
    addCitation: _temp
  });
  const documentPane = routeIsValid && sources.length ? <HouseholdWorkflowTrackersProvider householdID={submission.householdID}><HouseholdDocumentsViewerProvider householdId={submission.householdID} documentIds={sources.map(_temp2)} hideAddDocument={true} overrideActiveDocumentId={citations.activeDocumentId} overrideDocumentViewerActiveTab="document" onUpdateActiveDocumentId={t2 => {
      const {
        documentId
      } = t2;
      return citations.selectDocument(documentId);
    }}><HouseholdDocumentsViewer overrideAnnotations={citations.annotations} overrideActiveAnnotationIndex={citations.activeAnnotationIndex} hideDocumentSummarySwitch={true} hideDocumentSearch={true} hideDocumentRightBorder={true} /></HouseholdDocumentsViewerProvider></HouseholdWorkflowTrackersProvider> : undefined;
  const t3 = submission?.formVersion.form.name;
  let t4;
  if ($[7] !== formId || $[8] !== navigate || $[9] !== versionId) {
    t4 = () => navigate(ROUTE_KEYS.ADMIN_FORM_TEST_RUN, {
      formId,
      versionId
    });
    $[7] = formId;
    $[8] = navigate;
    $[9] = versionId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = citations.focusedField?.id;
  let t6;
  if ($[11] !== citations.close || $[12] !== citations.focusField || $[13] !== citations.selectEvidence || $[14] !== citations.selectedEvidence || $[15] !== documentPane || $[16] !== state || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <AnswerReviewWorkbench formName={t3} state={state} documentPane={documentPane} onClose={t4} onFocusField={citations.focusField} onCloseField={citations.close} focusedFieldId={t5} onSelectEvidence={citations.selectEvidence} selectedEvidence={citations.selectedEvidence} />;
    $[11] = citations.close;
    $[12] = citations.focusField;
    $[13] = citations.selectEvidence;
    $[14] = citations.selectedEvidence;
    $[15] = documentPane;
    $[16] = state;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}
function _temp2(t0) {
  const {
    id
  } = t0;
  return id;
}
function _temp() {}